<template>
  <div
    class="image-previewer"
    :class="[isMobile && 'image-previewer-h5']"
  >
    <div
      ref="image"
      class="image-wrapper"
      @touchstart.stop="handleTouchStart"
      @touchmove.stop="handleTouchMove"
      @touchend.stop="handleTouchEnd"
      @touchcancel.stop="handleTouchCancel"
      @wheel.stop="handleWheel"
    >
      <ul
        v-if="!props.imageUrl"
        ref="ulRef"
        class="image-list"
        :style="{
          width: `${imageList.length * 100}%`,
          transform: `translateX(-${
            (currentImageIndex * 100) / imageList.length
          }%)`,
          transition: '0.5s',
        }"
      >
        <li
          v-for="(item, index) in imageList"
          :key="index"
          class="image-item"
        >
          <ImageItem
            :zoom="zoom"
            :rotate="rotate"
            :translateX="translateX"
            :translateY="translateY"
            :src="getImageUrl(item)"
            :messageItem="item"
          />
        </li>
      </ul>
      <ul
        v-else
        ref="ulRef"
        class="image-list"
        :style="{ width: '100%' }"
      >
        <li class="image-item">
          <ImageItem
            :zoom="zoom"
            :rotate="rotate"
            :translateX="translateX"
            :translateY="translateY"
            :src="props.imageUrl"
          />
        </li>
      </ul>
    </div>
    <div
      class="icon icon-close"
      @click="close"
    >
      <Icon
        :file="iconClose"
        width="14px"
        height="14px"
      />
    </div>
    <div
      v-if="isPC && !props.imageUrl && currentImageIndex > 0"
      class="image-button image-button-left"
      @click="goPrev"
    >
      <Icon :file="iconArrowLeft" />
    </div>
    <div
      v-if="isPC && !props.imageUrl && currentImageIndex < imageList.length - 1"
      class="image-button image-button-right"
      @click="goNext"
    >
      <Icon :file="iconArrowLeft" />
    </div>
    <div
      v-if="isPC"
      :class="['actions-bar', isMobile && 'actions-bar-h5']"
    >
      <div
        class="icon-zoom-in"
        @click="zoomIn"
      >
        <Icon
          :file="iconZoomIn"
          width="27px"
          height="27px"
        />
      </div>
      <div
        class="icon-zoom-out"
        @click="zoomOut"
      >
        <Icon
          :file="iconZoomOut"
          width="27px"
          height="27px"
        />
      </div>
      <div
        class="icon-refresh-left"
        @click="rotateLeft"
      >
        <Icon
          :file="iconRotateLeft"
          width="27px"
          height="27px"
        />
      </div>
      <div
        class="icon-refresh-right"
        @click="rotateRight"
      >
        <Icon
          :file="iconRotateRight"
          width="27px"
          height="27px"
        />
      </div>
      <span v-if="!props.imageUrl" class="image-counter">
        {{ currentImageIndex + 1 }} / {{ imageList.length }}
      </span>
    </div>
    <div
      class="save"
      @click.stop.prevent="save"
    >
      <Icon
        :file="iconDownload"
        width="20px"
        height="20px"
      />
    </div>
  </div>
</template>

<script lang="ts">
import vue from '../../../adapter-vue';
import { IMessageModel, TUITranslateService } from '../../../@aidesk/uikit-engine';
import Icon from '../Icon.vue';
import iconClose from '../../../assets/icon-close.svg';
import iconArrowLeft from '../../../assets/icon-arrow-left.svg';
import iconZoomIn from '../../../assets/zoom-in.svg';
import iconZoomOut from '../../../assets/zoom-out.svg';
import iconRotateLeft from '../../../assets/rotate-left.svg';
import iconRotateRight from '../../../assets/rotate-right.svg';
import iconDownload from '../../../assets/download.svg';
import ImageItem from './image-item-web.vue';
import { Toast, TOAST_TYPE } from '../Toast/index-web';
import { isPC, isMobile } from '../../../utils/env';
interface touchesPosition {
  pageX1?: number;
  pageY1?: number;
  pageX2?: number;
  pageY2?: number;
}
const __sfc_main = {};
__sfc_main.props = {
  imageList: {
    key: "imageList",
    required: false,
    type: Array,
    default: () => [] as IMessageModel[]
  },
  currentImage: {
    key: "currentImage",
    required: true,
    type: null
  },
  imageUrl: {
    key: "imageUrl",
    required: false,
    type: String,
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    ref,
    watchEffect,
    onMounted,
    onUnmounted,
    withDefaults
  } = vue;
  const props = __props;
  const imageFormatMap = new Map([[1, 'jpg'], [2, 'gif'], [3, 'png'], [4, 'bmp']]);
  const emit = __ctx.emit;
  const zoom = ref(1);
  const rotate = ref(0);
  const minZoom = ref(0.1);
  const currentImageIndex = ref(0);
  const image = ref();
  const ulRef = ref();
  const translateX = ref(0);
  const translateY = ref(0);
  // touch
  let startX = 0;
  let startY = 0;
  let baseTranslateX = 0;
  let baseTranslateY = 0;
  const touchStore = {} as touchesPosition;
  let moveFlag = false;
  let twoTouchesFlag = false;
  let timer: number | null = null;
  // double tap to zoom
  let lastTapTime = 0;
  let lastTapX = 0;
  let lastTapY = 0;
  let singleTapTimer: number | null = null;
  let dblTapStep = 0;
  const DOUBLE_TAP_DELAY = 300;
  const DOUBLE_TAP_DIST = 30;
  const TAP_MOVE_THRESHOLD = 10;
  const DBL_TAP_ZOOM_LEVELS = [2, 3.5];
  watchEffect(() => {
    if (props.imageUrl) {
      currentImageIndex.value = 0;
      return;
    }
    currentImageIndex.value = props.imageList.findIndex((message: any) => {
      return message.ID === props?.currentImage?.ID;
    });
  });
  const isNumber = (value: any) => {
    return typeof value === 'number' && isFinite(value);
  };
  const handleTouchStart = (e: any) => {
    e.preventDefault();
    moveInit(e);
    twoTouchesInit(e);
  };
  const handleTouchMove = (e: any) => {
    e.preventDefault();
    moveFlag = true;
    if (e.touches && e.touches.length === 2) {
      twoTouchesFlag = true;
      handleTwoTouches(e);
    } else if (e.touches && e.touches.length === 1 && zoom.value > 1) {
      handleOneTouchPan(e);
    }
  };
  const handleTouchEnd = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    let moveEndX = 0;
    let X = 0;
    if (twoTouchesFlag) {
      if (!timer) {
        twoTouchesFlag = false;
        delete touchStore.pageX2;
        delete touchStore.pageY2;
        timer = setTimeout(() => {
          timer = null;
        }, 200);
      }
      return;
    }
    // H5 touch move to left to go to prev image
    // H5 touch move to right to go to next image
    const endX = e?.changedTouches[0]?.pageX ?? 0;
    const endY = e?.changedTouches[0]?.pageY ?? 0;
    const isTap = Math.hypot(endX - startX, endY - startY) < TAP_MOVE_THRESHOLD;
    if (moveFlag && !isTap) {
      if (timer === null) {
        moveEndX = endX;
        X = moveEndX - startX;
        if (zoom.value <= 1 && !props.imageUrl) {
          if (X > 100) {
            goPrev();
          } else if (X < -100) {
            goNext();
          }
        }
        timer = setTimeout(() => {
          timer = null;
        }, 200);
      }
    } else {
      const now = Date.now();
      const dist = Math.hypot(endX - lastTapX, endY - lastTapY);
      if (now - lastTapTime < DOUBLE_TAP_DELAY && dist < DOUBLE_TAP_DIST) {
        if (singleTapTimer !== null) {
          clearTimeout(singleTapTimer);
          singleTapTimer = null;
        }
        lastTapTime = 0;
        dblTapZoom(endX, endY);
      } else {
        lastTapTime = now;
        lastTapX = endX;
        lastTapY = endY;
        if (singleTapTimer !== null) {
          clearTimeout(singleTapTimer);
        }
        singleTapTimer = setTimeout(() => {
          singleTapTimer = null;
          close();
        }, DOUBLE_TAP_DELAY);
      }
    }
  };
  const handleTouchCancel = () => {
    twoTouchesFlag = false;
    delete touchStore.pageX1;
    delete touchStore.pageY1;
  };
  const handleWheel = (e: any) => {
    e.preventDefault();
    if (Math.abs(e.deltaX) !== 0 && Math.abs(e.deltaY) !== 0) return;
    let scale = zoom.value;
    scale += e.deltaY * (e.ctrlKey ? -0.01 : 0.002);
    scale = Math.min(Math.max(0.125, scale), 4);
    zoom.value = scale;
  };
  const moveInit = (e: any) => {
    startX = e?.changedTouches[0]?.pageX;
    startY = e?.changedTouches[0]?.pageY;
    baseTranslateX = translateX.value;
    baseTranslateY = translateY.value;
    moveFlag = false;
  };
  const handleOneTouchPan = (e: any) => {
    const touch = e?.touches[0];
    if (!touch) return;
    translateX.value = baseTranslateX + (touch.pageX - startX);
    translateY.value = baseTranslateY + (touch.pageY - startY);
  };
  const twoTouchesInit = (e: any) => {
    const touch1 = e?.touches[0];
    const touch2 = e?.touches[1];
    touchStore.pageX1 = touch1?.pageX;
    touchStore.pageY1 = touch1?.pageY;
    if (touch2) {
      touchStore.pageX2 = touch2?.pageX;
      touchStore.pageY2 = touch2?.pageY;
    }
  };
  const handleTwoTouches = (e: any) => {
    const touch1 = e?.touches[0];
    const touch2 = e?.touches[1];
    if (touch2) {
      if (!isNumber(touchStore.pageX2)) {
        touchStore.pageX2 = touch2.pageX;
      }
      if (!isNumber(touchStore.pageY2)) {
        touchStore.pageY2 = touch2.pageY;
      }
    }
    const getDistance = (startX: number, startY: number, stopX: number, stopY: number) => {
      return Math.hypot(stopX - startX, stopY - startY);
    };
    if (!isNumber(touchStore.pageX1) || !isNumber(touchStore.pageY1) || !isNumber(touchStore.pageX2) || !isNumber(touchStore.pageY2)) {
      return;
    }
    const touchZoom = getDistance(touch1.pageX, touch1.pageY, touch2.pageX, touch2.pageY) / getDistance(touchStore.pageX1 as number, touchStore.pageY1 as number, touchStore.pageX2 as number, touchStore.pageY2 as number);
    const newZoom = Math.min(Math.max(0.5, zoom.value * touchZoom), 4);
    // anchor zoom at the midpoint of two fingers
    const wrapperRect = image.value?.getBoundingClientRect?.();
    if (wrapperRect) {
      const centerX = wrapperRect.left + wrapperRect.width / 2;
      const centerY = wrapperRect.top + wrapperRect.height / 2;
      const midX = (touch1.pageX + touch2.pageX) / 2;
      const midY = (touch1.pageY + touch2.pageY) / 2;
      const ratio = newZoom / zoom.value;
      translateX.value = midX - centerX - (midX - centerX - translateX.value) * ratio;
      translateY.value = midY - centerY - (midY - centerY - translateY.value) * ratio;
    }
    zoom.value = newZoom;
    // refresh anchor for the next move frame
    touchStore.pageX1 = touch1.pageX;
    touchStore.pageY1 = touch1.pageY;
    touchStore.pageX2 = touch2.pageX;
    touchStore.pageY2 = touch2.pageY;
  };
  onMounted(() => {
    // web: close on esc keydown
    document?.addEventListener && document?.addEventListener('keydown', handleEsc);
  });
  const handleEsc = (e: any) => {
    e.preventDefault();
    if (e?.keyCode === 27) {
      close();
    }
  };
  const zoomIn = () => {
    zoom.value += 0.1;
  };
  const zoomOut = () => {
    zoom.value = zoom.value - 0.1 > minZoom.value ? zoom.value - 0.1 : minZoom.value;
  };
  const close = () => {
    emit('close');
  };
  const rotateLeft = () => {
    rotate.value -= 90;
  };
  const rotateRight = () => {
    rotate.value += 90;
  };
  const goNext = () => {
    currentImageIndex.value < props.imageList.length - 1 && currentImageIndex.value++;
    initStyle();
  };
  const goPrev = () => {
    currentImageIndex.value > 0 && currentImageIndex.value--;
    initStyle();
  };
  const initStyle = () => {
    zoom.value = 1;
    rotate.value = 0;
    translateX.value = 0;
    translateY.value = 0;
    dblTapStep = 0;
    lastTapTime = 0;
  };

  // 1st double tap: zoom to 1.5x; 2nd: 2.5x; 3rd: reset to 1x
  const dblTapZoom = (x: number, y: number) => {
    const wrapperRect = image.value?.getBoundingClientRect?.();
    if (!wrapperRect) return;
    if (dblTapStep >= DBL_TAP_ZOOM_LEVELS.length) {
      // reset to original size
      zoom.value = 1;
      translateX.value = 0;
      translateY.value = 0;
      dblTapStep = 0;
      return;
    }
    const targetZoom = DBL_TAP_ZOOM_LEVELS[dblTapStep];
    // anchor zoom at the double tap point (same formula as handleTwoTouches)
    const centerX = wrapperRect.left + wrapperRect.width / 2;
    const centerY = wrapperRect.top + wrapperRect.height / 2;
    const ratio = targetZoom / zoom.value;
    translateX.value = x - centerX - (x - centerX - translateX.value) * ratio;
    translateY.value = y - centerY - (y - centerY - translateY.value) * ratio;
    zoom.value = targetZoom;
    dblTapStep += 1;
  };
  const getImageUrl = (message: IMessageModel) => {
    if (props.imageUrl) {
      return props.imageUrl;
    }
    if (isPC) {
      return message?.payload?.imageInfoArray[0]?.url;
    } else {
      return message?.payload?.imageInfoArray[2]?.url;
    }
  };
  const save = () => {
    // bare url preview mode: download the url directly
    if (props.imageUrl) {
      downloadImgInWeb(props.imageUrl);
      return;
    }
    const imageMessage = props.imageList[currentImageIndex.value] as IMessageModel;
    const imageSrc = imageMessage?.payload?.imageInfoArray[0]?.url;
    if (!imageSrc) {
      Toast({
        message: TUITranslateService.t('Component.图片 url 不存在'),
        type: TOAST_TYPE.ERROR
      });
      return;
    }
    downloadImgInWeb(imageSrc);
  };
  const getExtFromUrl = (src: string) => {
    const pathname = src.split('?')[0].split('#')[0];
    const dotIndex = pathname.lastIndexOf('.');
    if (dotIndex < 0) return 'jpg';
    const ext = pathname.slice(dotIndex + 1).toLowerCase();
    return ext && ext.length <= 5 ? ext : 'jpg';
  };
  const downloadImgInWeb = (src: string) => {
    const option: any = {
      mode: 'cors',
      headers: new Headers({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };
    let extension: string;
    if (props.imageUrl) {
      // bare url preview mode: derive extension from url
      extension = getExtFromUrl(src);
    } else {
      const imageMessage = props.imageList[currentImageIndex.value] as IMessageModel;
      const imageFormat: number = imageMessage?.payload?.imageFormat;
      if (!imageFormatMap.has(imageFormat)) {
        Toast({
          message: TUITranslateService.t('Component.暂不支持下载此类型图片'),
          type: TOAST_TYPE.ERROR
        });
        return;
      }
      extension = imageFormatMap.get(imageFormat) as string;
    }
    // If the browser supports fetch, use blob to download, so as to avoid the browser clicking the a tag and jumping to the preview of the new page
    if ((window as any).fetch) {
      fetch(src, option).then(res => res.blob()).then(blob => {
        const a = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = url + '.' + extension;
        a.click();
      });
    } else {
      const a = document.createElement('a');
      a.href = src;
      a.target = '_blank';
      a.download = src + '.' + extension;
      a.click();
    }
  };
  onUnmounted(() => {
    document?.removeEventListener && document?.removeEventListener('keydown', handleEsc);
  });
  return {
    iconClose,
    iconArrowLeft,
    iconZoomIn,
    iconZoomOut,
    iconRotateLeft,
    iconRotateRight,
    iconDownload,
    isPC,
    isMobile,
    props,
    zoom,
    rotate,
    currentImageIndex,
    image,
    ulRef,
    translateX,
    translateY,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    handleWheel,
    zoomIn,
    zoomOut,
    close,
    rotateLeft,
    rotateRight,
    goNext,
    goPrev,
    getImageUrl,
    save
  };
};
__sfc_main.components = Object.assign({
  ImageItem,
  Icon
}, __sfc_main.components);
export default __sfc_main;
</script>

<style lang="scss" scoped>
@import "../common";

.actions-bar {
  display: flex;
  justify-content: space-around;
  align-items: center;
  position: absolute;
  bottom: 5%;
  padding: 12px;
  border-radius: 6px;
  background: #fff;

  &-h5 {
    padding: 6px;
  }

  .icon {
    position: static;
    font-size: 24px;
    cursor: pointer;
    width: 27px;
    height: 27px;
    margin: 5px;
  }

  .icon-zoom-in,
  .icon-zoom-out,
  .icon-refresh-left,
  .icon-refresh-right {
    cursor: pointer;
    user-select: none;
    padding: 5px;
  }
}

.image-previewer {
  position: fixed;
  z-index: 9999;
  width: 100vw;
  height: 100vh;
  background: rgba(#000, 0.3);
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;

  .image-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .image-list {
    position: absolute;
    height: 100%;
    left: 0;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: row;
    place-content: center center;

    .image-item {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
  }

  .image-preview {
    max-width: 100%;
    max-height: 100%;
    transition: transform 0.1s ease 0s;
    pointer-events: auto;
  }

  .image-button {
    display: flex;
    flex-direction: column;
    min-width: auto;
    justify-content: center;
    align-items: center;
    position: absolute;
    cursor: pointer;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    top: calc(50% - 20px);
    background: rgba(255, 255, 255, 0.8);

    &-left {
      left: 10px;
    }

    &-right {
      right: 10px;
      transform: rotate(180deg);
    }

    .icon {
      position: absolute;
      bottom: 0;
      top: 0;
      left: 0;
      right: 0;
      margin: auto;
      line-height: 40px;
      display: flex;
      flex-direction: column;
      min-width: auto;
      justify-content: center;
      align-items: center;
    }
  }

  .icon-close {
    position: absolute;
    cursor: pointer;
    border-radius: 50%;
    top: 3%;
    right: 3%;
    padding: 8px;
    background: #fff;
    display: flex;

    &::before,
    &::after {
      background-color: #444;
    }
  }
}

.image-previewer-h5 {
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  flex-direction: column;
}

.save {
  cursor: pointer;
  display: flex;
  justify-content: space-around;
  align-items: center;
  position: absolute;
  bottom: 5%;
  right: 5%;
  padding: 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.8);
}

.image-counter {
  background: rgba(20, 18, 20, 0.53);
  padding: 3px 5px;
  margin: 5px;
  border-radius: 3px;
  color: #fff;
}
</style>
